import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiJson } from '@/lib/apiClient';

import { plansQueryKey } from '@/features/plans/usePlans';
import { tenantsQueryKey } from './useTenants';

/**
 * Customer offboarding mutations (slice 27).
 *
 * Types mirror the wire contract in `api/src/modules/admin/routes.ts` — the
 * `CloseTenant` / `Reopen` / `ReleaseFleet` schemas (seam S4). Three separate
 * super-admin operations, and the split between them is the whole design:
 *
 *   - CLOSE blocks the customer's access (401 via `authorizePrincipal`) and
 *     touches NO hardware. Fully REVERSIBLE with reopen.
 *   - RELEASE FLEET returns the trackers and SIMs to inventory. It hides the
 *     fleet, and hidden means hidden — so slice 26's reports EXCLUDE released
 *     vehicles afterwards. Any data export must run BEFORE release. The dialog
 *     that calls `useReleaseFleet` states this in plain words; it is the one
 *     part of the feature that can genuinely lose someone's data.
 *
 * Both lists are invalidated on success — the tenant list carries the closed
 * state and the vehicle count, and the plans screen lists the same tenants.
 */

/** `POST …/close` → `{ id, closedAt }`. `closedAt` is the closure instant (the
 *  ORIGINAL one if the account was already closed — close is idempotent). */
export interface CloseTenantResponse {
  id: string;
  closedAt: string;
}

/** `POST …/reopen` → `{ id, closedAt: null }`. */
export interface ReopenTenantResponse {
  id: string;
  closedAt: null;
}

/** One vehicle that failed to release, with its `InventoryError` code, so a
 *  partial run is visible and can be re-run (the loop is idempotent). */
export interface ReleaseFailure {
  vehicleId: string;
  code: string;
}

/** `POST …/release-fleet` → a per-vehicle TALLY, not a boolean: the Traccar seam
 *  is un-transactable, so a partial run is a real outcome. `skipped` counts
 *  vehicles already released by an earlier run — re-running is safe and finishes
 *  a partial run. */
export interface ReleaseFleetResult {
  released: number;
  skipped: number;
  failed: ReleaseFailure[];
}

export function useCloseTenant(
  tenantId: string,
): UseMutationResult<CloseTenantResponse, Error, { reason: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      apiJson<CloseTenantResponse>(`/api/admin/tenants/${tenantId}/close`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
      void qc.invalidateQueries({ queryKey: plansQueryKey });
    },
  });
}

export function useReopenTenant(
  tenantId: string,
): UseMutationResult<ReopenTenantResponse, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<ReopenTenantResponse>(`/api/admin/tenants/${tenantId}/reopen`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
      void qc.invalidateQueries({ queryKey: plansQueryKey });
    },
  });
}

export function useReleaseFleet(
  tenantId: string,
): UseMutationResult<ReleaseFleetResult, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<ReleaseFleetResult>(`/api/admin/tenants/${tenantId}/release-fleet`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    // The vehicle count on the tenant list drops as trackers return to stock.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
      void qc.invalidateQueries({ queryKey: plansQueryKey });
    },
  });
}
